import * as React from 'react'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { SelectionGroup } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { DriverAvatarIcon } from '@/app/features/icons/DriverAvatar'
import useFormatDate from '@/modules/drivers/hooks/useFormatDate'
import type { Driver } from '@/lib/api/company/drivers/types'
import type { BillingPeriod, DriverBillingStats } from '../types'
import { faChevronDownSolid, FaIcon } from '@/lib/fa'
import BillingDriverEventsRow from './BillingDriverEventsRow'
import TimesheetCalcsCell from './TimesheetCalcsCell'

interface BillingDriverRowProps {
  driver: Driver
  stats: DriverBillingStats | undefined
  companyId: string
  period: BillingPeriod
  selectedIds: SelectionGroup
  toggleId: (id: string | number, isActive?: boolean) => void
  isSelectedAny: boolean
}

const fmt = (n: number | undefined): string =>
  n == null ? '-' : new Intl.NumberFormat().format(n)

const BillingDriverRow: React.FC<BillingDriverRowProps> = ({
  driver,
  stats,
  companyId,
  period,
  selectedIds,
  toggleId,
  isSelectedAny,
}) => {
  const { t } = useLocalize()
  const { formatDate } = useFormatDate()
  const [isHovered, setIsHovered] = React.useState(false)
  const [isExpanded, setIsExpanded] = React.useState(false)

  const isChecked = selectedIds?.ids.includes(driver.id)
  const isInactive = !driver.isActive
  const hasEvents = !!stats?.lastEventAt
  // Prefer the resolved display name, fall back to email, then raw user id so
  // we never end up rendering a numeric identifier in a user-facing cell.
  const lastBy = stats?.lastEventByUserName
    ?? stats?.lastEventByUserEmail
    ?? stats?.lastEventByUserId
    ?? '-'
  const lastByTooltip = stats?.lastEventByUserEmail
    && stats.lastEventByUserName
    && stats.lastEventByUserEmail !== stats.lastEventByUserName
    ? stats.lastEventByUserEmail
    : undefined

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!hasEvents) return
    setIsExpanded((v) => !v)
  }

  const handleRowClick = () => {
    if (!hasEvents) return
    setIsExpanded((v) => !v)
  }

  return (
    <>
      <tr
        className={`notches-border--right tw-w-fit ${hasEvents ? 'tw-cursor-pointer' : ''} tw-border-b tw-bg-white hover:tw-bg-primary/10 ${isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''} ${isInactive ? 'tw-bg-gray-100 tw-text-gray-500' : ''}`}
        onClick={handleRowClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TableCheckbox
          checked={isChecked}
          onChange={() => toggleId(driver.id, driver.isActive)}
          isHovered={isHovered}
        />
        <TableTextRow
          icon={<DriverAvatarIcon className="tw-h-5 tw-w-5 tw-text-content-base" />}
        >
          {driver.fullName || `${driver.surname ?? ''} ${driver.name ?? ''}`.trim() || '-'}
        </TableTextRow>
        <TableTextRow>
          <span className={driver.isActive ? 'tw-text-green-600' : 'tw-text-yellow-600'}>
            {driver.isActive ? t('common.activeM') : t('common.inactiveM')}
          </span>
        </TableTextRow>
        <TableTextRow>
          <TimesheetCalcsCell
            total={stats?.timesheetCalculationsCount}
            recalculations={stats?.timesheetRecalculationsCount}
          />
        </TableTextRow>
        <TableTextRow>{fmt(stats?.importedActivityDays)}</TableTextRow>
        <TableTextRow>{fmt(stats?.printoutsGeneratedCount)}</TableTextRow>
        <TableTextRow>{fmt(stats?.exportsGeneratedCount)}</TableTextRow>
        <TableTextRow>
          {stats?.lastEventAt ? formatDate(stats.lastEventAt) : '-'}
        </TableTextRow>
        <TableTextRow>
          <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2">
            <span title={lastByTooltip} className="tw-truncate">{lastBy}</span>
            {hasEvents && (
              <button
                type="button"
                onClick={toggleExpanded}
                aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
                className={`tw-flex-shrink-0 tw-rounded-lg tw-bg-secondary tw-p-1 ${isExpanded ? 'tw-rotate-180' : ''} tw-transition-transform`}
              >
                <FaIcon className="tw-h-4 tw-w-4" icon={faChevronDownSolid} />
              </button>
            )}
          </div>
        </TableTextRow>
      </tr>
      {isExpanded && hasEvents && (
        <BillingDriverEventsRow
          companyId={companyId}
          driverId={driver.id}
          period={period}
          colSpan={9}
        />
      )}
    </>
  )
}

export default BillingDriverRow
